/**
 * RegionalSales — bucket 2 of 6, rebuilt to `regional-sales.jsx`
 * (BON-82). KPI strip (weekly sales w/ scope picker · ad spend · web
 * visits · QR scans) over a 1.55fr/1fr grid:
 *
 *   left:  Event Pacing Monitor · Ad Spend by Platform · Problem Workshops
 *   right: Regional Web Traffic · QR Scans by Venue · By Sub-region
 *
 * Every module the DTO doesn't feed yet sits behind <MockedOverlay>
 * citing BON-138. The previous funnel / sales-by-venue / channels
 * modules were not in the design (BON-82 delta: "extra") and are
 * removed; the conversion funnel can return with the BON-138 data if
 * product wants it back.
 */
import type { CSSProperties } from 'react';

import { BucketError, BucketLoading } from './BucketStates';
import type { RegionalBucketProps } from './regional-bucket';
import { useRegionalSales } from './regionalApi';
import { RegionalSalesAdSpend } from './RegionalSalesAdSpend';
import { RegionalSalesKpis } from './RegionalSalesKpis';
import { RegionalSalesPacing } from './RegionalSalesPacing';
import { RegionalSalesProblems } from './RegionalSalesProblems';
import { RegionalSalesQr } from './RegionalSalesQr';
import { RegionalSalesSubregions } from './RegionalSalesSubregions';
import { RegionalSalesTraffic } from './RegionalSalesTraffic';

const COLUMN: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
  minWidth: 0,
};

export function RegionalSales({ regionSlug }: RegionalBucketProps) {
  const q = useRegionalSales(regionSlug);

  if (q.isLoading) return <BucketLoading />;
  if (q.isError) return <BucketError error={q.error} />;
  if (!q.data) return null;
  const { data } = q.data;

  return (
    <>
      <RegionalSalesKpis kpis={data.kpis} />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.55fr) minmax(0, 1fr)',
          gap: 14,
        }}
      >
        <div style={COLUMN}>
          <RegionalSalesPacing pacing={data.pacing} />
          <RegionalSalesAdSpend adSpend={data.adSpend} />
          <RegionalSalesProblems problems={data.problemWorkshops} />
        </div>
        <div style={COLUMN}>
          <RegionalSalesTraffic />
          <RegionalSalesQr perVenue={data.perVenue} />
          <RegionalSalesSubregions />
        </div>
      </div>
    </>
  );
}
